import type { Blueprint } from '#layers/studio/app/runtime/builder/base/blueprints/Blueprint';
import type { ValidatableBlueprint } from '#layers/studio/app/runtime/builder/base/blueprints/ValidatableBlueprint';
import type { HasWidth } from '#layers/studio/app/runtime/builder/base/traits/HasWidth';
import type { ValidationErrors } from '#layers/studio/app/runtime/builder/base/types/ValidationErrors';
import type { FormBuilderContract } from '#layers/studio/app/runtime/builder/form';
import type { BlueprintDefinition } from '#layers/studio/app/runtime/builder/form/blueprints/BlueprintDefinition';
import type { CustomErrorBlueprint } from '#layers/studio/app/runtime/builder/form/blueprints/CustomErrorBlueprint';
import type { ReadonlyBlueprint } from '#layers/studio/app/runtime/builder/form/blueprints/ReadonlyBlueprint';
import type { RequiredBlueprint } from '#layers/studio/app/runtime/builder/form/blueprints/RequiredBlueprint';
import type { VisibleBlueprint } from '#layers/studio/app/runtime/builder/form/blueprints/VisibleBlueprint';
import type { Entry } from '#layers/studio/app/runtime/builder/form/entries/Entry';
import { entry, instanceOfEntry } from '#layers/studio/app/runtime/builder/form/entries/Entry';
import { ValidEntry } from '#layers/studio/app/runtime/builder/form/entries/ValidEntry';
import { AlwaysChoice } from '#layers/studio/app/runtime/builder/form/enums/AlwaysChoice';
import type { InternallyChoice } from '#layers/studio/app/runtime/builder/form/enums/InternallyChoice';
import { NeverChoice } from '#layers/studio/app/runtime/builder/form/enums/NeverChoice';
import type { WhenChoice } from '#layers/studio/app/runtime/builder/form/enums/WhenChoice';
import type { EntryFactory } from '#layers/studio/app/runtime/builder/form/traits/EntryFactory';
import type { HasHelp } from '#layers/studio/app/runtime/builder/form/traits/HasHelp';
import type { HasLabel } from '#layers/studio/app/runtime/builder/form/traits/HasLabel';
import type { ProcessCallback } from '#layers/studio/app/runtime/builder/form/types/ProcessCallback';

export const Definition: BlueprintDefinition = {
    type: 'edito-page',
    name: '[[[Strona]]]',
    icon: 'fa-browser',
    group: 'cms',
    position: 20
};

export class EditoPageData
{
    public websiteId?: number = null;
    public locale?: string = null;
    public pageId?: number = null;
    public path?: string = '';
    public url?: string = '';
}

export class EditoPageEntry extends ValidEntry<EditoPageData>
{
    public type: string = Definition.type;
    public data: EditoPageData = {};

    public constructor(data: any = null)
    {
        super();

        if (data !== null)
        {
            this.data = data;
        }
    }

    public async collect(blueprint: EditoPageContract, form: FormBuilderContract, preprocess: ProcessCallback): Promise<Entry>
    {
        return entry({
            type: this.type,
            data: {
                websiteId: this.data.websiteId,
                locale: this.data.locale,
                pageId: this.data.pageId
            }
        });
    }

    public validate(blueprint: EditoPageContract, form: FormBuilderContract): boolean
    {
        this.errors = {};

        if (!form.expressions.readonly(blueprint, true) && form.expressions.visible(blueprint, true))
        {
            const required = form.expressions.required(blueprint);
            const empty = this.data?.pageId == undefined;

            if (required && empty)
            {
                this.errors.value = [`[[[Pole "%0" jest wymagane.|||${form.localization.translate(blueprint.label)}]]]`];
            }
            else if (form.expressions.customError(blueprint))
            {
                this.errors.custom = [form.expressions.customErrorMessage(blueprint)];
            }
        }

        return this.valid();
    }
}

export const instanceOfEditoPageEntry = (object: any): object is EditoPageEntry =>
{
    return instanceOfEntry(object) && 'type' in object && object.type === Definition.type;
};

export interface EditoPageContract extends Blueprint, VisibleBlueprint, ReadonlyBlueprint, RequiredBlueprint, CustomErrorBlueprint, HasLabel, HasHelp, HasWidth
{
}

export class EditoPageType implements EditoPageContract, ValidatableBlueprint, EntryFactory<EditoPageEntry>
{
    public id: string;
    public type: string;
    public name: string;
    public label: Record<string, string>;
    public showLabel: boolean;
    public help: Record<string, string>;
    public minWidth: number;
    public width: number;
    public visible: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public visibleWhen: string;
    public errors: ValidationErrors;
    public required: AlwaysChoice | NeverChoice | WhenChoice;
    public requiredWhen: string;
    public customError: NeverChoice | WhenChoice;
    public customErrorWhen: string;
    public customErrorMessage: Record<string, string>;
    public readonly: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public readonlyWhen: string;

    public constructor(id: string, name: string)
    {
        this.id = id;
        this.type = Definition.type;
        this.name = name;
        this.label = { 'pl-PL': 'Strona' };
        this.help = {};
        this.showLabel = true;
        this.minWidth = 1;
        this.width = 0;
        this.visible = AlwaysChoice.Always;
        this.visibleWhen = null;
        this.errors = {};
        this.customError = NeverChoice.Never;
        this.customErrorWhen = null;
        this.customErrorMessage = {};
        this.readonly = NeverChoice.Never;
        this.readonlyWhen = null;
        this.required = NeverChoice.Never;
        this.requiredWhen = null;
        this.visible = AlwaysChoice.Always;
        this.visibleWhen = null;
    }

    public setDefaultWidth(width: number): void
    {
        this.width = Math.min(3, Math.max(this.minWidth, width));
    }

    public createEntry(data: any): EditoPageEntry
    {
        return new EditoPageEntry(data);
    }

    public validate(): Record<string, ValidationErrors>
    {
        this.errors = {};

        return {
            [this.name]: this.errors
        };
    }
}
